import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { AppRoutes } from "./routes";
import { SplashScreen } from "./components/SplashScreen";
import { apiFetch } from "./lib/api";
import { E2E_MOCK } from "./lib/e2eMock";
import { markShown, wasShown } from "./lib/notifyDedupe";
import { flushPendingUnregister } from "./lib/push";
import { toastForOutboxKind } from "./lib/outboxToast";
import { showToast } from "./lib/toast";
import { trackDailyEvent } from "./lib/track";

export function App() {
  // 앱 부팅 시 솟다 스플래시를 1회 표시 → 끝나면 본화면으로 (E2E 빌드는 스킵)
  const [splashDone, setSplashDone] = useState(E2E_MOCK);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let disposed = false;
    let handles: Array<{ remove: () => Promise<void> }> = [];

    // FCM 토큰을 서버에 등록(플랫폼 구분). 권한 요청은 genDebate(첫 토론 시)에서 하고,
    // 여기서는 이미 허용된 상태의 토큰 등록·갱신만 담당한다.
    const sendToken = (token?: string | null) => {
      if (!token) return;
      void apiFetch("/api/mobile/push/register", {
        method: "POST",
        body: { token, platform: Capacitor.getPlatform() },
      }).catch((error) => {
        console.error("[push] 토큰 서버 등록 실패", error);
      });
    };

    // 🔴 N-3: 지난 로그아웃에서 파기하지 못한 토큰이 남아 있으면 지금 처리한다.
    //   (세션이 유효할 때만 성공한다 — 실패분은 큐에 남아 다음 기회를 기다린다.)
    void flushPendingUnregister();

    void Promise.all([
      // FCM registration token 발급/갱신 이벤트 → 서버 등록
      FirebaseMessaging.addListener("tokenReceived", (event) => sendToken(event.token)),
      // 알림 탭 시 딥링크 이동 + 중복 표출 차단 등록.
      // ⚠ iOS의 notificationReceived는 **포그라운드에서만** 불린다(백그라운드는 silent push 전용).
      //    즉 백그라운드 배너로 본 알림은 이 핸들러 말고는 dedupe Set에 등록될 길이 없어,
      //    배너를 탭해 들어와도 전역 폴링(useNotificationOutbox)이 같은 내용을 토스트로 또 띄운다.
      FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
        const data = event.notification?.data as Record<string, unknown> | undefined;
        const tappedKind = typeof data?.kind === "string" ? data.kind : "";
        const tappedRefId = typeof data?.ref_id === "string" ? data.ref_id : "";
        if (tappedKind && tappedRefId) markShown(tappedKind, tappedRefId);
        const link = data?.link;
        if (typeof link === "string" && link.startsWith("/")) {
          window.location.hash = `#${link}`;
        }
      }),
      // 포그라운드 수신은 시스템 배너 대신 전역 인앱 토스트로 표시한다.
      FirebaseMessaging.addListener("notificationReceived", (event) => {
        const data = event.notification?.data as Record<string, unknown> | undefined;
        const kind = typeof data?.kind === "string" ? data.kind : "";
        const refId = typeof data?.ref_id === "string" ? data.ref_id : "";
        if (kind && refId && wasShown(kind, refId)) return;
        if (kind && refId) markShown(kind, refId);
        // 서버 kind로 토스트 종류를 가른다 — 폴링 경로(useNotificationOutbox)와 같은 정본을 쓴다.
        // 종전엔 'achievement' 하드코딩이라 상폐 청산도 록이 축하 알약으로 떴다.
        const mapped = toastForOutboxKind(kind);
        showToast({
          kind: mapped.kind,
          expr: mapped.expr,
          title: event.notification?.title ?? "알림",
          subtitle: event.notification?.body ?? null,
          ...(typeof data?.link === "string" && data.link.startsWith("/") ? { link: data.link } : {}),
        });
      }),
    ]).then((registeredHandles) => {
      if (disposed) {
        void Promise.all(registeredHandles.map((handle) => handle.remove()));
        return;
      }
      handles = registeredHandles;
    }).catch((error) => {
      console.error("[push] 알림 리스너 연결 실패", error);
    });

    // 이미 권한을 허용한 사용자는 앱 시작 시 토큰을 등록/갱신(재설치·토큰 로테이션 대응).
    // 권한이 아직 없으면 여기서 요청하지 않는다(개인정보 #4: 허용 전 등록 API 미호출).
    void FirebaseMessaging.checkPermissions()
      .then((perm) => (perm.receive === "granted" ? FirebaseMessaging.getToken() : null))
      .then((result) => sendToken(result?.token))
      .catch((error) => console.error("[push] 초기 토큰 등록 실패", error));

    return () => {
      disposed = true;
      void Promise.all(handles.map((handle) => handle.remove()));
    };
  }, []);

  /* ── L2 리텐션 계측 — 하루 첫 진입 1건 ────────────────────────────
   * 왜 필요한가: 기존 이벤트 10종이 **전부 "최초 1회" 마일스톤**이라
   *   재방문·빈도·이탈을 잴 수 없다. `first_review`는 "첫 복기를 했다"만 알려주지
   *   두 번째를 했는지는 모른다 — 앱의 핵심 가설을 지금 계측으로는 검증할 수 없다.
   *
   * ⚠ 부트 경로에서 supabase를 **기다리지 않는다.** auth 좀비 락으로 오프라인에서
   *   앱이 고착된 사고가 있었다(2026-07-26). 그래서:
   *     - 동적 import (번들·초기화를 첫 페인트 밖으로)
   *     - 지연 후 실행 (세션 복원과 경합하지 않게)
   *     - await 없음 · 실패는 삼킨다 (계측이 앱을 막으면 안 된다)
   *
   * 하루 1건 판정은 **서버가 한다**(log_daily_event RPC). localStorage로 하면
   *   기기마다 다르고 지우면 중복이 들어온다. 비로그인은 서버가 조용히 false를 준다.
   */
  useEffect(() => {
    const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
    // 3초 지연 — 세션 복원과 경합하지 않게. trackDailyEvent 자체가 await 없이 실패를 삼킨다.
    const timer = setTimeout(() => trackDailyEvent("app_open", { platform }), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <AppRoutes />
      {!splashDone && <SplashScreen onFinish={() => setSplashDone(true)} />}
    </>
  );
}
